/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  computed,
  inject,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  DynamicFormsService,
  IDynamicForm,
} from '@kinet-open-network/client/core/forms/util';
import {
  clearDirtyState,
  setDirtyState,
} from '@kinet-open-network/client/shared/util';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core';
import { ButtonModule } from 'primeng/button';
import { pairwise, startWith } from 'rxjs';

export interface DynamicFormOptions {
  submitText?: string;
  buttonClass?: string;
  buttonIcon?: string;
  buttonHidden?: boolean;
  buttonDisabled?: boolean;
  formState?: any;
  buttonSeverity?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warn'
    | 'help'
    | 'danger';
}

export interface DynamicFormField extends FormlyFieldConfig {}

let DYNAMIC_FORM_ID = 0;

@Component({
  selector: 'kon-dynamic-form',
  imports: [FormlyModule, ReactiveFormsModule, ButtonModule],
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()" [id]="formId()">
      <formly-form
        [form]="form"
        [fields]="_fields()"
        [options]="formlyOptions"
        [model]="model"
        (modelChange)="modelChange.emit($event)"
      ></formly-form>
      <ng-content />

      @if (!options.buttonHidden) {
        <div
          class="sticky bottom-4 h-fit w-full justify-self-end my-4 rounded-md"
        >
          <p-button
            type="submit"
            [class]="options.buttonClass || 'grid'"
            [loading]="loading() || form.pending"
            [label]="options.submitText || 'Uložiť'"
            [disabled]="buttonDisabled || !form.valid"
            [icon]="options.buttonIcon || ''"
            [severity]="options.buttonSeverity"
          ></p-button>
        </div>
      }
    </form>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormComponent implements OnInit, OnDestroy, IDynamicForm {
  private formsService = inject(DynamicFormsService);
  private route = inject(ActivatedRoute);

  @Input() form = new FormGroup({});

  private _model: any = {};

  fields = input<DynamicFormField[]>([]);
  trackDirtyState = input(true);
  loading = input(false);
  formId = input(`dynamic-form-${DYNAMIC_FORM_ID++}`);

  readonly _fields: Signal<DynamicFormField[]> = computed(() => {
    const disableFieldsRecursively = (
      fields: DynamicFormField[],
    ): DynamicFormField[] => {
      return fields.map((field) => {
        const updatedField: DynamicFormField =
          field.fieldGroup || field.fieldArray
            ? field
            : {
                ...field,
                expressions: {
                  ...field.expressions,
                  'props.disabled':
                    (field?.expressions?.['props.disabled'] as string) ??
                    field?.props?.disabled?.toString() ??
                    'formState.readonly',
                },
              };

        if (field.fieldGroup) {
          updatedField.fieldGroup = disableFieldsRecursively(field.fieldGroup);
        } else if (field.fieldArray && typeof field.fieldArray !== 'function') {
          updatedField.fieldArray = {
            ...field.fieldArray,
            fieldGroup: disableFieldsRecursively(
              field.fieldArray.fieldGroup ?? [],
            ),
          };
        }

        return updatedField;
      });
    };

    return disableFieldsRecursively(this.fields());
  });

  formlyOptions: FormlyFormOptions = {};

  private _options: DynamicFormOptions = {};

  @Input() set options(value: DynamicFormOptions) {
    if (value.formState) {
      this.formlyOptions.formState = value.formState;
    }

    this._options = { ...value };
  }
  get options(): DynamicFormOptions {
    return this._options;
  }

  get buttonDisabled() {
    if (this.options.buttonDisabled) {
      return true;
    }

    if (this.formlyOptions?.formState?.disabled !== undefined) {
      if (typeof this.options.formState.disabled === 'function') {
        return this.options.formState.disabled();
      }

      return !!this.options.formState.disabled;
    }

    return false;
  }

  @Input() set model(value: any) {
    this._model = { ...value };
  }
  get model(): any {
    return this._model;
  }

  @Output() modelChange = new EventEmitter<any>();

  @Output() submitted = new EventEmitter<any>();

  constructor() {
    toObservable(this.formId)
      .pipe(pairwise(), startWith([null, null]))
      .subscribe(([oldId, newId]) => {
        if (oldId) {
          clearDirtyState(this.route, oldId);
        }

        if (newId) {
          this.recomputeDirtyState();
        }
      });
  }

  reset(model?: any) {
    this.formlyOptions.resetModel?.(model);
  }

  updateInitialModel(model?: any) {
    this.formlyOptions.updateInitialValue?.(model);
  }

  ngOnInit(): void {
    this.formsService.addForm(this);
    this.form.valueChanges.subscribe(() => this.recomputeDirtyState());
  }

  ngOnDestroy(): void {
    this.formsService.removeForm(this);
    this.purgeDirtyState();
  }

  onSubmit(): void {
    this.purgeDirtyState();
    this.submitted.emit({ ...this.model });
  }

  setValue(value: any): void {
    this.model = { ...value };
  }

  purgeDirtyState() {
    clearDirtyState(this.route, this.formId());
  }

  private recomputeDirtyState() {
    const key = this.formId();
    const isDirty = this.trackDirtyState() && this.form.dirty;

    if (isDirty) {
      setDirtyState(this.route, key, true);
    } else {
      clearDirtyState(this.route, key);
    }
  }
}
